import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Film, Video, Watch, Moon, Settings, LogOut, ChevronRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { avatarUrl } from '../../utils/avatar';

/**
 * MobileMoreSheet — the "You" bottom sheet (mobile/tablet only). Holds the
 * overflow destinations that aren't primary tabs: profile, tools, cycle,
 * settings, log out. Tapping any row closes the sheet.
 */
const Row = ({ to, icon: Icon, label, onClick, testId, danger }) => {
  const cls = `w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors text-left ${danger ? 'text-red-400 hover:bg-red-500/10' : 'text-zinc-300 hover:bg-zinc-800/60'}`;
  const body = (
    <>
      <Icon className="w-5 h-5 shrink-0" />
      <span className="flex-1 text-sm font-secondary">{label}</span>
      {!danger && <ChevronRight className="w-4 h-4 text-zinc-600" />}
    </>
  );
  if (onClick) return <button type="button" onClick={onClick} className={cls} data-testid={testId}>{body}</button>;
  return <Link to={to} className={cls} data-testid={testId}>{body}</Link>;
};

const MobileMoreSheet = ({ open, onClose }) => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  if (!open) return null;
  const photo = avatarUrl(user, token);

  const handleLogout = () => { onClose(); logout(); navigate('/'); };

  return (
    <div className="lg:hidden fixed inset-0 z-[60]" data-testid="mobile-more-sheet">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute bottom-0 inset-x-0 bg-surface border-t border-zinc-800 rounded-t-2xl pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="relative flex items-center justify-between px-4 pt-5 pb-2">
          <span className="absolute left-1/2 -translate-x-1/2 top-2 w-10 h-1 bg-zinc-700 rounded-full" />
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-zinc-800 overflow-hidden flex items-center justify-center">
              {photo ? <img src={photo} alt="You" className="w-full h-full object-cover" /> : <span className="font-bold">{user?.name?.charAt(0) || 'U'}</span>}
            </div>
            <div>
              <p className="font-semibold text-sm">{user?.name || 'User'}</p>
              <p className="text-xs text-zinc-500 uppercase">{user?.role || 'Athlete'}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="more-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-2 py-2 space-y-0.5 max-h-[60vh] overflow-y-auto" onClick={onClose}>
          <p className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-widest text-zinc-600">Tools</p>
          <Row to="/analyze" icon={Film} label="Movement Analyser" testId="more-analyser" />
          <Row to="/live-sessions" icon={Video} label="Live Sessions" testId="more-live-sessions" />
          <Row to="/wearables" icon={Watch} label="Wearables" testId="more-wearables" />
          <Row to="/cycle" icon={Moon} label="Cycle" testId="more-cycle" />
          <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-zinc-600">Account</p>
          <Row to="/profile" icon={Settings} label="Settings" testId="more-settings" />
          <Row onClick={handleLogout} icon={LogOut} label="Log out" testId="more-logout" danger />
        </div>
      </div>
    </div>
  );
};

export default MobileMoreSheet;
